/**
 * Fetches Shopify's product recommendations and swaps them in.
 *
 * `recommendations.products` is only populated when a section is requested
 * through the recommendations route, so the section renders empty and asks for
 * itself. The response is a full section render; only its inner markup is
 * taken, which keeps every decision about layout, headings and empty states in
 * Liquid where the rest of the theme's rendering lives.
 *
 * Hand-picked bundles carry no data-url — they are already server-rendered —
 * so this does nothing on those.
 *
 * The request fires on connect rather than waiting for the section to near the
 * viewport. It is one small request behind a deferred script, already off the
 * critical path, and deferring it further would mean the grid arrives while
 * the visitor is looking at it.
 *
 * Wrapped in a function because a product page carries two copies of this
 * section — related products and a cross-sell row — and each emits its own
 * script tag. The browser fetches the file once but runs it once per tag, and
 * a second run of a top-level `class` in the same global scope is a redeclare:
 * it threw on every product page before this.
 */
(() => {
  if (customElements.get('product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const { url } = this.dataset;
      if (!url || this.loaded) return;

      this.loaded = true;
      this.controller = new AbortController();
      this.load(url);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async load(url) {
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));

        const markup = await response.text();
        const fresh = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('product-recommendations');

        // No recommendations is a perfectly normal answer — a new product, or
        // a catalogue too small to draw from. Leave the section as it is:
        // empty, and therefore invisible.
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          // reveal.js scans once at load, so cards arriving after that would
          // stay at opacity 0 forever. See the same note in facets.js.
          for (const el of this.querySelectorAll('.reveal')) el.classList.add('is-visible');
          for (const img of this.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
        }
      } catch (error) {
        // A failed recommendation is not worth a broken page.
        if (error.name !== 'AbortError') this.removeAttribute('data-url');
      }
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
})();
